import React, { useState, useMemo } from "react"
import { Form, Button, Checkbox, Flex, Modal } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Title, Text } from "ui/Typography"
import { CustomInput, PhoneInput } from "ui/Input"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { spacings } from "themes/variables"
import { CustomForm } from "ui/Form"
import { routes } from "router"
import { updateUserInfo } from "store/user"
import { TermsAndConditions } from "../SignUp/TermsAndConditions"
import { PrivacyPolicy } from "../SignUp/PrivacyPolicy"
import useFormErrors from "hooks/useAntdFormErrors"
import { CustomInputError } from "ui/CustomInputError"
import { isProApp } from "utils/app-type"
import { toaster } from "components/common/Toast"

export const ConfirmInfo = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefill = useMemo(() => {
    return JSON.parse(sessionStorage.getItem("oauth_prefill") || "{}")
  }, [])
  const oauth_type = searchParams.get("oauth_type")
  const email = prefill?.email ?? searchParams.get("email")
  const first_name = prefill?.first_name ?? searchParams.get("first_name")
  const last_name = prefill?.last_name ?? searchParams.get("last_name")
  const rawPhone = prefill?.phone_number ?? searchParams.get("phone_number")
  const { temp_token } = useSelector((state) => state.auth)
  const { loading: updateUserInfoLoading } = useSelector((state) => state.user.updateUserInfo)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalType, setModalType] = useState(null)
  const [form] = Form.useForm()
  const isProHost = isProApp()

  const initialPhone = useMemo(() => {
    if (!rawPhone || rawPhone === "null") return null
    if (typeof rawPhone === "object") return rawPhone
    if (typeof rawPhone === "string") {
      try {
        const parsed = JSON.parse(rawPhone)
        if (parsed?.fullNumber) return parsed
      } catch (e) {
        console.warn("ConfirmInfo: failed to parse rawPhone JSON, treating as plain string:", rawPhone, e)
      }
      return { fullNumber: rawPhone }
    }
    return null
  }, [rawPhone])

  const showModal = (type) => {
    setModalType(type)
    setIsModalOpen(true)
  }
  const handleCancel = () => {
    setModalType(null)
    setIsModalOpen(false)
  }

  const onFinish = (user) => {
    // Normalize phone_number to string fullNumber before sending/storing
    const phoneValue = user?.phone_number?.fullNumber ?? user?.phone_number ?? null
    const payload = { ...user, phone_number: phoneValue, encrypted_uuid: temp_token }
    delete payload?.email
    dispatch(updateUserInfo(payload)).then((response) => {
      if (response?.message) {
        toaster.success(response.message)
      }
      sessionStorage.removeItem("oauth_prefill")
      navigate(routes.confirmPhone(), { state: { phone_number: phoneValue }, replace: true })
    })
  }

  const onInputChange = (value) => {
    // store the whole phone object so form holds { fullNumber, initialCountryCode }
    form.setFieldsValue({
      phone_number: value
    })
  }

  const { errors } = useFormErrors(form)

  return (
    <>
      <Title className="text-center mb-3xl" level={2}>
        {t(`sign_in.${oauth_type}_confirm_title`)}
      </Title>
      <CustomForm
        name="confirm-info"
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          first_name: first_name && first_name !== "null" ? first_name : "",
          last_name: last_name && last_name !== "null" ? last_name : "",
          email,
          phone_number: initialPhone // store the object as initial value
        }}
      >
        <Flex gap={spacings.S}>
          <Form.Item
            label={t("sign_up.first_name")}
            name="first_name"
            required={true}
            rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            className="flex-grow-1"
          >
            <CustomInput placeholder={t("sign_up.name")} />
          </Form.Item>
          <Form.Item
            label={t("sign_up.last_name")}
            name="last_name"
            required={true}
            rules={[{ required: true, message: t("form_validation.cannot_be_blank") }]}
            className="flex-grow-1"
          >
            <CustomInput placeholder={t("sign_up.last_name")} />
          </Form.Item>
        </Flex>
        {isProHost && (
          <Form.Item label={t("sign_up.company")} name="company_name" className="flex-grow-1">
            <CustomInput placeholder={t("sign_up.company")} />
          </Form.Item>
        )}
        <Form.Item
          label={t("sign_up.email")}
          name="email"
          required={true}
          hidden={email && email !== "null"}
          rules={[
            { required: true, message: t("form_validation.cannot_be_blank") },
            { type: "email", message: t("form_validation.invalid_email") }
          ]}
          help={errors?.email ? <CustomInputError errorKey={errors.email} /> : null}
        >
          <CustomInput placeholder={t("sign_up.email_placeholder")} />
        </Form.Item>

        <Form.Item
          label={t("sign_up.phone_number")}
          name="phone_number"
          required={true}
          rules={[
            { required: true, message: t("form_validation.phone_number_invalid") },
            { validator: (_, value) => validatePhoneNumber(value?.fullNumber) }
          ]}
        >
          <PhoneInput
            key={initialPhone?.fullNumber}
            initialValue={initialPhone?.fullNumber}
            initialCountryCode={initialPhone?.initialCountryCode}
            onChange={onInputChange}
          />
        </Form.Item>

        <Form.Item
          name="agreeCheck"
          valuePropName="checked"
          rules={[
            {
              validator: (_, value) => (value ? Promise.resolve() : Promise.reject(new Error(t("form_validation.accept_terms_conditions"))))
            }
          ]}
        >
          <Checkbox>
            <Text type="secondary" variant="body">
              {t("sign_up.agree_terms")}
            </Text>
            <Text className="ml-xs" link>
              <Link onClick={() => showModal("TermsAndConditions")}>{t("sign_up.terms_conditions")}</Link>
            </Text>
            <Text className="ml-xs" link>
              &
            </Text>
            <Text className="ml-xs" link>
              <Link onClick={() => showModal("PrivacyPolicy")}>{t("sign_up.privacy_policy")}</Link>
            </Text>
          </Checkbox>
        </Form.Item>

        <Form.Item>
          <Button
            className="mt-xs mb-xs"
            type="primary"
            htmlType="submit"
            block
            size="large"
            disabled={updateUserInfoLoading}
            loading={updateUserInfoLoading}
          >
            {t("sign_in.confirm_and_create")}
          </Button>
        </Form.Item>
      </CustomForm>
      <Flex justify="center">
        <Text type="secondary" variant="body">
          {t("sign_up.already_have_account")}
        </Text>
        <Text className="ml-xs" link>
          <Link to={routes.signInV2()}>{isProHost ? t("sign_in.log_in") : t("sign_in.log_in_as_guest")}</Link>
        </Text>
      </Flex>
      <Modal
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={1022}
        style={{
          top: 40
        }}
      >
        {modalType === "TermsAndConditions" ? <TermsAndConditions /> : <PrivacyPolicy />}
      </Modal>
    </>
  )
}
